import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Check, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setPolling(false); return; }

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setStatus(data);
          if (data.payment_status === 'paid' || data.status === 'expired') {
            setPolling(false);
            return;
          }
        }
      } catch (e) { console.error(e); }

      setAttempts(prev => {
        if (prev >= 10) { setPolling(false); return prev; }
        return prev + 1;
      });
    };

    poll();
    const interval = setInterval(() => {
      if (attempts < 10) poll();
      else { clearInterval(interval); setPolling(false); }
    }, 2000);

    return () => clearInterval(interval);
  }, [sessionId, attempts]);

  const isPaid = status?.payment_status === 'paid';
  const isExpired = status?.status === 'expired';

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet><title>Payment {isPaid ? 'Complete' : 'Processing'} — Kojiru</title></Helmet>
      <Navbar />

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto text-center">
          {!sessionId ? (
            <div>
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">No Session Found</h1>
              <p className="text-gray-500 mb-6">No payment session ID in the URL.</p>
              <Link to="/aaas"><Button className="bg-emerald-600 hover:bg-emerald-700">Back to AaaS</Button></Link>
            </div>
          ) : polling ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Loader2 className="w-12 h-12 text-emerald-400 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold mb-2">Processing Payment...</h1>
              <p className="text-gray-500">Confirming with Stripe. This takes a few seconds.</p>
            </motion.div>
          ) : isPaid ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="payment-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-emerald-400 mb-2">Payment Complete!</h1>
              <p className="text-gray-400 mb-2">{status.metadata?.package_name || 'Your purchase'} is now active.</p>
              {status.amount_total && <p className="text-sm text-gray-500 mb-6">${(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}</p>}
              <div className="flex gap-3 justify-center">
                <Link to="/aaas"><Button className="bg-emerald-600 hover:bg-emerald-700">Go to AaaS Dashboard <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              </div>
            </motion.div>
          ) : isExpired ? (
            <div>
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-red-400 mb-2">Payment Expired</h1>
              <p className="text-gray-500 mb-6">The session expired. Please try again.</p>
              <Link to="/aaas"><Button className="bg-emerald-600 hover:bg-emerald-700">Try Again</Button></Link>
            </div>
          ) : (
            <div>
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Payment Status Unknown</h1>
              <p className="text-gray-500 mb-6">We couldn't confirm your payment. Please check your email or contact support.</p>
              <Link to="/aaas"><Button className="bg-emerald-600 hover:bg-emerald-700">Back to AaaS</Button></Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
